// A dataset's ingest settings, as edited in the web app: `ingestMetadata` (how datasetIngestFunction reads
// what a client sends), `duplicatePolicy` and `curationMode`. See docs/plans/DATASET_API_INGEST_PLAN.md.
//
// The shape of ingestMetadata is deliberately NOT enforced here while it is still settling — the editor only
// requires a JSON object. datasetIngestFunction (src/ingestSettings.js) is the one validator: settings it
// can't use reject the upload with the reasons, which show in the dataset's upload history.
import type { SelectOption } from "../components/ReusableComponents/Select";
import { mappingProblem, parseMappingText } from "./datasetColumnMapping";

export interface DatasetIngestSettingsForm {
  ingestMetadataText: string;
  duplicatePolicy: string;
  curationMode: string;
}

export interface DatasetIngestSettingsInput {
  ingestMetadata: string | null;
  duplicatePolicy: string | null;
  curationMode: string | null;
}

interface DatasetWithIngestSettings {
  ingestMetadata?: unknown;
  duplicatePolicy?: string | null;
  curationMode?: string | null;
  parameterType?: string | null;
  units?: string | null;
}

export interface IngestColumnInfo {
  /** What a chart calls the column; the stored name when unset */
  label?: string;
  unit?: string;
  description?: string;
}

/** Column labels, units and descriptions from ingestMetadata, keyed by the STORED column name (what charts see). */
export type IngestColumnInfoMap = Record<string, IngestColumnInfo>;

// "" is unset: ingest then uses the default named in the label
export const DUPLICATE_POLICY_OPTIONS: SelectOption[] = [
  { value: "", label: "Default (latest cell wins)" },
  { value: "LATEST_CELL", label: "Latest cell wins — a later upload overwrites only the cells it contains" },
  { value: "LATEST_ROW", label: "Latest row wins — a later upload replaces the whole row" },
  { value: "REJECT", label: "Reject repeated timestamps" },
  { value: "KEEP_ALL", label: "Keep all" },
  { value: "REPLACE_WINDOW", label: "Replace window — an upload replaces everything in the window it declares (a model's runs)" }
];

export const CURATION_MODE_OPTIONS: SelectOption[] = [
  { value: "", label: "Default (none)" },
  { value: "NONE", label: "None — one processed CSV per upload" },
  { value: "PARQUET", label: "Parquet — uploads are also curated to Parquet" }
];

export const INGEST_METADATA_EXAMPLE = JSON.stringify(
  {
    timestamp: { source: "thymestamp", timezone: "America/Los_Angeles", format: "M/d/yyyy H:mm" },
    columns: [
      {
        source: "Ross Q (cfs)",
        name: "ross_discharge",
        label: "Ross discharge",
        type: "number",
        unit: "ft3 s-1",
        description: "Ross Dam plant discharge"
      }
    ],
    unmappedColumns: "keep"
  },
  null,
  2
);

/** Stored AWSJSON (a string, sometimes stringified twice, or already an object) → pretty text for the editor. */
export const formatIngestMetadata = (stored: unknown): string => {
  let value = stored;
  for (let i = 0; i < 2 && typeof value === "string"; i++) {
    try {
      value = JSON.parse(value);
    } catch {
      // Not JSON: show it as stored so it can be fixed
      return String(stored);
    }
  }
  if (value === null || value === undefined || value === "") return "";
  return JSON.stringify(value, null, 2);
};

/** Editor text → the AWSJSON string to store (null when blank), or why it can't be saved. */
export const parseIngestMetadataText = (text: string): { value: string | null; error: string | null } => {
  if (!text.trim()) return { value: null, error: null };
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    return { value: null, error: `Not valid JSON: ${(err as Error).message}` };
  }
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { value: null, error: "Ingest settings must be a JSON object: { … }" };
  }
  return { value: JSON.stringify(parsed), error: null };
};

export const ingestSettingsFormFromDataset = (dataset: DatasetWithIngestSettings | null | undefined): DatasetIngestSettingsForm => ({
  ingestMetadataText: formatIngestMetadata(dataset?.ingestMetadata),
  duplicatePolicy: dataset?.duplicatePolicy || "",
  curationMode: dataset?.curationMode || ""
});

/**
 * The updateDataset fields for the form, or the error that blocks saving. Only fields the user CHANGED since
 * the form was opened are included: the form is seeded from a cached copy of the dataset, so re-sending
 * untouched values could overwrite settings saved elsewhere in the meantime (the harness, another tab).
 */
export const ingestSettingsInputFromForm = (
  form: DatasetIngestSettingsForm,
  initial: DatasetIngestSettingsForm
): { input: Partial<DatasetIngestSettingsInput> | null; error: string | null } => {
  const { value, error } = parseIngestMetadataText(form.ingestMetadataText);
  if (error) return { input: null, error };
  // Only a mapping the user changed can block Save: an untouched one is never re-sent, so a problem in it must
  // not stop an unrelated edit (the name, the picture)
  const problem =
    form.ingestMetadataText !== initial.ingestMetadataText ? mappingProblem(parseMappingText(form.ingestMetadataText).mapping) : null;
  if (problem) return { input: null, error: problem };
  const input: Partial<DatasetIngestSettingsInput> = {};
  if (form.ingestMetadataText !== initial.ingestMetadataText) input.ingestMetadata = value;
  if (form.duplicatePolicy !== initial.duplicatePolicy) input.duplicatePolicy = form.duplicatePolicy || null;
  if (form.curationMode !== initial.curationMode) input.curationMode = form.curationMode || null;
  return { input, error: null };
};

export const hasIngestSettings = (dataset: DatasetWithIngestSettings | null | undefined): boolean =>
  Boolean(formatIngestMetadata(dataset?.ingestMetadata) || dataset?.duplicatePolicy || dataset?.curationMode);

const parseStoredIngestMetadata = (stored: unknown): Record<string, unknown> | null => {
  let value = stored;
  for (let i = 0; i < 2 && typeof value === "string"; i++) {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value !== null && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
};

/**
 * The zone ingest applies to timestamps that carry no offset, when the upload names none itself:
 * `timestamp.timezone` from the dataset's ingest settings, or null (ingest then reads them as UTC, with a
 * warning on the upload). Shown by the upload modal so the uploader knows what the default means.
 */
export const getIngestTimezone = (dataset: DatasetWithIngestSettings | null | undefined): string | null => {
  const timestamp = parseStoredIngestMetadata(dataset?.ingestMetadata)?.timestamp;
  if (!timestamp || typeof timestamp !== "object") return null;
  const zone = (timestamp as Record<string, unknown>).timezone;
  return typeof zone === "string" && zone.trim() ? zone.trim() : null;
};

/** Tolerant by design: anything that isn't a column with a name or source is skipped, never thrown on. */
export const getIngestColumnInfo = (dataset: DatasetWithIngestSettings | null | undefined): IngestColumnInfoMap => {
  const columns = parseStoredIngestMetadata(dataset?.ingestMetadata)?.columns;
  if (!Array.isArray(columns)) return {};
  const info: IngestColumnInfoMap = {};
  for (const column of columns) {
    if (!column || typeof column !== "object") continue;
    const { name, source, label, unit, description } = column as Record<string, unknown>;
    const stored = [name, source].find((v) => typeof v === "string" && v.trim()) as string | undefined;
    if (!stored) continue;
    const entry: IngestColumnInfo = {};
    if (typeof label === "string" && label.trim()) entry.label = label.trim();
    if (typeof unit === "string" && unit.trim()) entry.unit = unit.trim();
    if (typeof description === "string" && description.trim()) entry.description = description.trim();
    if (entry.label || entry.unit || entry.description) info[stored.trim()] = entry;
  }
  return info;
};

const NON_SERIES_KEYS = new Set(["id", "timestamp", "flag", "uploadId", "parameterId"]);

const isNumericCell = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== "" && !isNaN(Number(value));

/**
 * The columns ParameterHistoryGraph draws: every key seen on any row (first-seen order) that holds at least one
 * numeric value. Keys come from every row, not the first: wide datasets arrive in pieces, so rows can carry
 * different columns and a column missing from the first row is still plotted (#851).
 */
export const plottedColumnsOf = (rows: Array<Record<string, unknown>>): string[] => {
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    for (const key of Object.keys(row || {})) {
      if (!seen.has(key)) {
        seen.add(key);
        keys.push(key);
      }
    }
  }
  return keys.filter((key) => !NON_SERIES_KEYS.has(key) && rows.some((row) => isNumericCell(row?.[key])));
};

/**
 * Y-axis title for a dataset's chart. The dataset-wide "Parameter (units)" unless the plotted columns declare
 * their own units in the ingest settings: a single column is named with its unit, columns sharing one unit
 * show that unit, and mixed units leave the axis as "Value" (each series carries its unit in its label).
 */
export const describeDatasetYAxis = (
  dataset: DatasetWithIngestSettings | null | undefined,
  rows: Array<Record<string, unknown>> | null | undefined,
  columnInfo: IngestColumnInfoMap
): string => {
  // Datasets made before units moved onto columns still carry a dataset-wide parameter and unit
  const fallback = dataset?.parameterType ? (dataset?.units ? `${dataset.parameterType} (${dataset.units})` : dataset.parameterType) : "Value";
  const plotted = plottedColumnsOf(rows || []);
  const units = plotted
    .map((c) => columnInfo[c]?.unit)
    .filter((unit, i, all): unit is string => Boolean(unit) && all.indexOf(unit) === i);
  if (units.length === 0) return fallback;
  if (units.length > 1 || !plotted.every((c) => columnInfo[c]?.unit)) return "Value";
  return plotted.length === 1 ? `${columnInfo[plotted[0]]?.label || plotted[0]} (${units[0]})` : units[0];
};
